
import { useIsMobile } from "@/hooks/use-mobile";
import { useTransactionTable } from "./useTransactionTable";
import { TransactionTableSkeleton } from "./TransactionTableSkeleton";
import { MobileTransactionCard } from "./MobileTransactionCard";
import { DesktopTransactionTable } from "./DesktopTransactionTable";
import { DeleteTransactionDialog } from "./DeleteTransactionDialog";
import { EditTransactionDialog } from "./EditTransactionDialog";

export function TransactionTable() {
  const isMobile = useIsMobile();
  const {
    transactions,
    isLoading,
    isInitialLoad,
    loadError,
    deleteDialogOpen,
    setDeleteDialogOpen,
    editDialogOpen,
    setEditDialogOpen,
    selectedTransaction,
    handleEdit,
    handleDelete,
    confirmDelete,
    fetchTransactions,
    getStatusColor,
    getTypeColor,
    getTypeLabel,
    formatCurrency
  } = useTransactionTable();

  // Loading state showing cached data with overlay
  if (isLoading && isInitialLoad) {
    return <TransactionTableSkeleton />;
  }

  // Conditional rendering based on device
  if (isMobile) {
    return (
      <div className="px-2 py-4">
        {isLoading && !isInitialLoad && (
          <div className="fixed inset-0 bg-black/10 backdrop-blur-[1px] z-40 flex items-center justify-center pointer-events-none">
            <div className="bg-[#222831]/80 p-2 rounded-md text-white text-sm animate-pulse">
              Atualizando...
            </div>
          </div>
        )}
        
        {loadError && (
          <div className="mb-4 p-2 bg-red-500/10 text-red-500 rounded-md text-sm">
            Houve um problema ao carregar as transações. Os dados exibidos podem estar desatualizados.
            <button 
              onClick={() => fetchTransactions(true)}
              className="block w-full mt-2 text-center px-3 py-1 bg-red-500/20 hover:bg-red-500/30 rounded-md"
            >
              Tentar novamente
            </button>
          </div>
        )}
        
        {!transactions || transactions.length === 0 ? (
          <div className="text-center py-6 text-gray-400">
            Nenhuma transação encontrada
          </div>
        ) : (
          <div className="space-y-2">
            {transactions.map((transaction) => (
              <MobileTransactionCard 
                key={transaction.id} 
                transaction={transaction}
                getTypeColor={getTypeColor}
                getTypeLabel={getTypeLabel}
                getStatusColor={getStatusColor}
                formatCurrency={formatCurrency}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
              />
            ))}
          </div>
        )}
        
        <DeleteTransactionDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={confirmDelete}
        />

        <EditTransactionDialog
          isOpen={editDialogOpen}
          transaction={selectedTransaction}
          onClose={() => setEditDialogOpen(false)}
          onSuccess={() => {
            setEditDialogOpen(false);
            fetchTransactions(true);
          }}
        />
      </div>
    );
  }

  return (
    <>
      {isLoading && !isInitialLoad && (
        <div className="fixed inset-0 bg-black/10 backdrop-blur-[1px] z-40 flex items-center justify-center pointer-events-none">
          <div className="bg-[#222831]/80 p-3 rounded-md text-white animate-pulse">
            Atualizando transações...
          </div>
        </div>
      )}
      
      {loadError && (
        <div className="mx-4 my-2 p-3 bg-red-500/10 text-red-500 rounded-md flex justify-between items-center">
          <span>Houve um problema ao carregar as transações. Os dados exibidos podem estar desatualizados.</span>
          <button 
            onClick={() => fetchTransactions(true)}
            className="px-3 py-1 bg-red-500/20 hover:bg-red-500/30 rounded-md text-sm"
          >
            Tentar novamente
          </button>
        </div>
      )}
      
      <DesktopTransactionTable
        transactions={transactions}
        getTypeColor={getTypeColor}
        getTypeLabel={getTypeLabel}
        getStatusColor={getStatusColor}
        formatCurrency={formatCurrency}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
      />

      <DeleteTransactionDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={confirmDelete}
      />

      <EditTransactionDialog
        isOpen={editDialogOpen}
        transaction={selectedTransaction}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={() => {
          setEditDialogOpen(false);
          fetchTransactions(true);
        }}
      />
    </>
  );
}
